import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUserProfile, uploadProfileImage } from './utils/ProfileDataContext';
import './MyProfileEdit.css'; 

function ProfileEdit() {
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    id: '',
    bio: '',
    location: ''
  });

  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    async function fetchProfile() {
      try {
        const data = await fetchUserProfile();
        if (isMounted) {
          setProfile({
            firstName: data.firstName || '',
            lastName: data.lastName || data.secondName || '',
            email: data.email || '',
            phoneNumber: data.phoneNumber || '',
            id: data.id || '',
            bio: data.bio || '',
            location: data.location || ''
          });
          if (data.profileImage) {
            setImagePreview(data.profileImage);
            setUploadedImageUrl(data.profileImage);
          }
        }
      } catch (e) {
        if (isMounted) setError('Failed to load profile. Please try again.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchProfile();

    return () => { isMounted = false; };
  }, []);

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🔍 Save button clicked');
    
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Only upload image if a new one was selected
      if (profileImage) {
        console.log('📤 Uploading image...');
        const imageUrl = await uploadProfileImage(profileImage);
        
        // Store the image URL in localStorage
        if (imageUrl) {
          localStorage.setItem('userProfileImage', imageUrl);
          setUploadedImageUrl(imageUrl);
          
          // Update the profile state to show the new image
          const updatedProfile = {
            ...profile,
            profileImage: imageUrl
          };
          localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
        }
        
        console.log('✅ Image uploaded successfully');
        setSuccess(true);
        // Don't auto-redirect - let user decide when to close
      } else {
        // No new image selected
        setError('Please select an image to upload');
      }
      
    } catch (e) {
      console.error('❌ Save error:', e);
      setError(e.message || 'Failed to upload image. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > maxSize) {
      setError('Image size should be less than 5MB');
      return;
    }

    setProfileImage(file);
    setError(null);
    setSuccess(false); // Clear success message when new image is selected

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleImageRemove = () => {
    setProfileImage(null);
    setImagePreview(uploadedImageUrl || ''); // Revert to previously uploaded image
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setSuccess(false);
    setError(null);
  };

  const handleCancel = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Cancel button clicked - redirecting to dashboard');
    navigate('/dashboard');
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Close button clicked - redirecting to dashboard');
    navigate('/dashboard');
  };

  const handleViewProfile = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('View Profile button clicked');
    navigate('/profile');
  };

  const handleDone = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Done button clicked - redirecting to dashboard');
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-edit-container">
      <div className="profile-edit-card">
        {/* Header with close button */}
        <div className="profile-edit-header">
          <div className="header-content">
            <h2>Update Profile Photo</h2>
            <p className="subtitle">Your personal information is managed by the system administrator</p>
          </div>
          <button 
            className="close-button"
            onClick={handleClose}
            type="button"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Success message with Done button */}
        {success && (
          <div className="success-message with-action">
            <div className="success-content">
              <span className="success-icon">✅</span>
              <div className="success-text">
                <strong>Profile photo updated successfully!</strong>
                <p>Your new photo has been saved.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleDone}
              className="done-btn"
            >
              Done
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        <div className="form-content">
          {/* Profile Image Section */}
          <div className="profile-image-section">
            <div className="image-preview-container">
              {imagePreview ? (
                <>
                  <img 
                    src={imagePreview} 
                    alt="Profile preview" 
                    className="profile-image-preview"
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={handleImageRemove}
                    aria-label="Remove image"
                  >
                    ×
                  </button>
                </>
              ) : (
                <div className="profile-image-placeholder">
                  <span>No photo</span>
                  <small>Click "Choose Image" to upload</small>
                </div>
              )}
            </div>
            
            <div className="image-upload-controls">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                id="profileImage"
                style={{ display: 'none' }}
              />
              <label 
                htmlFor="profileImage" 
                className="upload-image-btn"
              >
                {imagePreview ? 'Change Photo' : 'Choose Image'}
              </label>
              <p className="image-hint">JPEG, PNG, GIF or WebP. Max 5MB</p>
            </div>
          </div>

          {/* Read-only Profile Information */}
          <div className="read-only-info">
            <h3>Your Profile Information</h3>
            <p className="info-note">
              <i>Contact your system administrator to update personal information</i>
            </p>
            
            <div className="info-grid">
              <div className="info-item">
                <label>First Name</label>
                <div className="info-value">{profile.firstName || 'Not set'}</div>
              </div>
              
              <div className="info-item">
                <label>Last Name</label>
                <div className="info-value">{profile.lastName || 'Not set'}</div>
              </div>
              
              <div className="info-item">
                <label>Email Address</label>
                <div className="info-value">{profile.email || 'Not set'}</div>
              </div>
              
              <div className="info-item">
                <label>Phone Number</label>
                <div className="info-value">{profile.phoneNumber || 'Not set'}</div>
              </div>
              
              {profile.bio && (
                <div className="info-item full-width">
                  <label>Bio</label>
                  <div className="info-value">{profile.bio}</div>
                </div>
              )}
              
              {profile.location && (
                <div className="info-item">
                  <label>Location</label>
                  <div className="info-value">{profile.location}</div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Conditionally rendered */}
          <div className="action-buttons">
            {!success ? (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="cancel-btn"
                  disabled={saving}
                >
                  Cancel
                </button>
                
                <button
                  type="button"
                  onClick={handleViewProfile}
                  className="secondary-btn"
                  disabled={saving}
                >
                  View Profile
                </button>
                
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !profileImage}
                  className="save-btn"
                >
                  {saving ? (
                    <>
                      <span className="spinner-small"></span>
                      Uploading...
                    </>
                  ) : 'Upload Photo'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleViewProfile}
                  className="secondary-btn"
                >
                  View Profile
                </button>
                
                <button
                  type="button"
                  onClick={handleDone}
                  className="save-btn"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileEdit;